/**
 * §KB — Amazon'un `confirmShipment` çağrısında tanıdığı taşıyıcı kodları.
 *
 * Liste, işletmenin gerçek rotasına göre seçildi: ABD deposundan Japonya'ya
 * uluslararası taşıyıcılar, Japonya içi son teslimat için yerel taşıyıcılar.
 *
 * ⚠️ Amazon tanımadığı bir kod gelirse bildirimi reddediyor. Listede olmayan
 * bir taşıyıcı için `OTHER` kodu ve serbest metin ad kullanılıyor — Amazon
 * bu ikiliyi kabul ediyor.
 */

import type { PazarKodu } from '../pazar/profil.js';

export interface TasiyiciSecenegi {
  /** Amazon'a giden kod */
  kod: string;
  /** Ekranda görünen ad — Amazon'a `carrierName` olarak da gidiyor */
  ad: string;
  /**
   * `PAZAR_ICI`: 18 Ekim 2026'da eklenen pazarların iç taşıyıcıları — ekranda
   * "<pazar adı> içi" başlığıyla görünür (her pazar için ayrı grup adı gerekmesin).
   */
  grup:
    | 'ULUSLARARASI'
    | 'JAPONYA'
    | 'BIRLESIK_KRALLIK'
    | 'MEKSIKA'
    | 'SUUDI_ARABISTAN'
    | 'PAZAR_ICI'
    | 'DIGER';
}

export const KARGO_TASIYICILARI: readonly TasiyiciSecenegi[] = [
  // ABD → Japonya
  { kod: 'DHL', ad: 'DHL', grup: 'ULUSLARARASI' },
  { kod: 'FEDEX', ad: 'FedEx', grup: 'ULUSLARARASI' },
  { kod: 'UPS', ad: 'UPS', grup: 'ULUSLARARASI' },
  { kod: 'USPS', ad: 'USPS', grup: 'ULUSLARARASI' },
  { kod: 'DHL_GLOBAL_MAIL', ad: 'DHL eCommerce', grup: 'ULUSLARARASI' },
  { kod: 'ARAMEX', ad: 'Aramex', grup: 'ULUSLARARASI' },
  { kod: 'TNT', ad: 'TNT', grup: 'ULUSLARARASI' },
  // Japonya içi
  { kod: 'YAMATO', ad: 'Yamato Transport (クロネコヤマト)', grup: 'JAPONYA' },
  { kod: 'SAGAWA', ad: 'Sagawa Express (佐川急便)', grup: 'JAPONYA' },
  { kod: 'JP_EXPRESS', ad: 'Japan Post (日本郵便)', grup: 'JAPONYA' },
  { kod: 'NITTSU', ad: 'Nippon Express (日本通運)', grup: 'JAPONYA' },
  { kod: 'SEINO', ad: 'Seino Transportation (西濃運輸)', grup: 'JAPONYA' },
  // Serbest
  { kod: 'OTHER', ad: 'Diğer (adını yazın)', grup: 'DIGER' },
];

/**
 * Birleşik Krallık içi son teslimat taşıyıcıları (13 Ekim 2026, UK paneli).
 *
 * Kodlar Amazon UK'nin taşıyıcı adları. Amazon bir kodu tanımazsa bildirim
 * görünür biçimde reddediliyor (gönderi FAILED); o durumda "Diğer" ile ad yazılır.
 */
const BIRLESIK_KRALLIK_TASIYICILARI: readonly TasiyiciSecenegi[] = [
  { kod: 'Royal Mail', ad: 'Royal Mail', grup: 'BIRLESIK_KRALLIK' },
  { kod: 'Parcelforce', ad: 'Parcelforce', grup: 'BIRLESIK_KRALLIK' },
  { kod: 'DPD', ad: 'DPD', grup: 'BIRLESIK_KRALLIK' },
  { kod: 'Evri', ad: 'Evri (Hermes)', grup: 'BIRLESIK_KRALLIK' },
  { kod: 'Yodel', ad: 'Yodel', grup: 'BIRLESIK_KRALLIK' },
];

/**
 * Meksika içi son teslimat taşıyıcıları (14 Ekim 2026, MX paneli). Kodlar
 * Amazon MX'in taşıyıcı adları; tanınmayan kod görünür biçimde reddedilir
 * (gönderi FAILED) — o durumda "Diğer" ile ad yazılır.
 */
const MEKSIKA_TASIYICILARI: readonly TasiyiciSecenegi[] = [
  { kod: 'Estafeta', ad: 'Estafeta', grup: 'MEKSIKA' },
  { kod: 'Redpack', ad: 'Redpack', grup: 'MEKSIKA' },
  { kod: 'Paquetexpress', ad: 'Paquetexpress', grup: 'MEKSIKA' },
  { kod: '99minutos', ad: '99minutos', grup: 'MEKSIKA' },
  { kod: 'Correos de Mexico', ad: 'Correos de México', grup: 'MEKSIKA' },
];

/**
 * Suudi Arabistan içi son teslimat taşıyıcıları (15 Ekim 2026, SA paneli). Aramex
 * uluslararası listede. Amazon SA'nın taşıyıcı kod tablosu herkese açık kaynakta
 * bulunamadı; kodlar taşıyıcıların yaygın adları. Tanınmayan kod görünür biçimde
 * reddedilir (gönderi FAILED) — o durumda "Diğer" ile ad yazılır.
 */
const SUUDI_ARABISTAN_TASIYICILARI: readonly TasiyiciSecenegi[] = [
  { kod: 'SMSA', ad: 'SMSA Express', grup: 'SUUDI_ARABISTAN' },
  { kod: 'Naqel', ad: 'Naqel Express', grup: 'SUUDI_ARABISTAN' },
  { kod: 'Saudi Post', ad: 'Saudi Post (SPL)', grup: 'SUUDI_ARABISTAN' },
];

/**
 * 18 Ekim 2026'da eklenen 17 pazarın iç taşıyıcıları. Kodlar taşıyıcıların Amazon'daki
 * yaygın adları (UK/MX/SA ile aynı yöntem); Amazon tanımadığı kodu görünür biçimde
 * reddediyor (gönderi FAILED) — o durumda "Diğer" ile ad yazılır. DHL, FedEx, UPS,
 * Aramex, TNT uluslararası listede.
 */
const ic = (...adlar: string[]): readonly TasiyiciSecenegi[] =>
  adlar.map((ad) => ({ kod: ad, ad, grup: 'PAZAR_ICI' as const }));

const YENI_PAZARLARIN_TASIYICILARI = {
  CA: ic('Canada Post', 'Purolator', 'Canpar', 'Loomis Express'),
  AU: ic('Australia Post', 'StarTrack', 'CouriersPlease', 'Sendle'),
  AE: ic('Emirates Post', 'iMile'),
  EG: ic('Egypt Post', 'Bosta'),
  BR: ic('Correios', 'Jadlog', 'Loggi', 'Total Express'),
  TR: ic('Yurtiçi Kargo', 'Aras Kargo', 'MNG Kargo', 'PTT Kargo', 'Sürat Kargo'),
  FR: ic('Colissimo', 'Chronopost', 'La Poste', 'Mondial Relay', 'GLS'),
  DE: ic('Deutsche Post', 'Hermes', 'DPD', 'GLS'),
  ES: ic('Correos', 'SEUR', 'MRW', 'GLS', 'Nacex'),
  IT: ic('Poste Italiane', 'BRT', 'SDA', 'GLS'),
  IE: ic('An Post', 'DPD', 'GLS', 'Fastway'),
  BE: ic('bpost', 'PostNL', 'DPD', 'GLS'),
  SE: ic('PostNord', 'DB Schenker', 'Bring', 'Budbee'),
  PL: ic('InPost', 'Poczta Polska', 'DPD', 'GLS'),
  NL: ic('PostNL', 'DPD', 'GLS'),
  IN: ic('India Post', 'Blue Dart', 'Delhivery', 'Ekart', 'DTDC'),
  SG: ic('Singapore Post', 'Ninja Van', 'J&T Express'),
} as const satisfies Record<Exclude<PazarKodu, 'JP' | 'UK' | 'MX' | 'SA'>, readonly TasiyiciSecenegi[]>;

/** Pazarın kendi iç taşıyıcıları (JP'de ayrı liste yok: JP listesi bugünküyle aynı kalıyor). */
const PAZAR_ICI: Record<Exclude<PazarKodu, 'JP'>, readonly TasiyiciSecenegi[]> = {
  UK: BIRLESIK_KRALLIK_TASIYICILARI,
  MX: MEKSIKA_TASIYICILARI,
  SA: SUUDI_ARABISTAN_TASIYICILARI,
  ...YENI_PAZARLARIN_TASIYICILARI,
};

/** Panelin pazarına göre liste: JP bugünkü listenin aynısı; diğerleri uluslararası + pazar içi + Diğer. */
export function pazarTasiyicilari(pazar: PazarKodu = 'JP'): readonly TasiyiciSecenegi[] {
  if (pazar === 'JP') return KARGO_TASIYICILARI;
  return [
    ...KARGO_TASIYICILARI.filter((t) => t.grup === 'ULUSLARARASI'),
    ...PAZAR_ICI[pazar],
    ...KARGO_TASIYICILARI.filter((t) => t.grup === 'DIGER'),
  ];
}

export function tasiyiciGecerliMi(kod: string, pazar: PazarKodu = 'JP'): boolean {
  return pazarTasiyicilari(pazar).some((t) => t.kod === kod);
}

export function tasiyiciAdi(kod: string): string | null {
  return (
    [
      ...KARGO_TASIYICILARI,
      ...BIRLESIK_KRALLIK_TASIYICILARI,
      ...MEKSIKA_TASIYICILARI,
      ...SUUDI_ARABISTAN_TASIYICILARI,
      ...Object.values(YENI_PAZARLARIN_TASIYICILARI).flat(),
    ].find((t) => t.kod === kod)?.ad ?? null
  );
}

/**
 * Takip numarası biçim kontrolü.
 *
 * Amazon numarayı doğrulamıyor; yanlış numara sessizce kabul ediliyor ve
 * müşteri gönderisini takip edemiyor. Burada yalnızca bariz hatalar
 * yakalanıyor: boşluk/tire dışında işaret, çok kısa, çok uzun.
 */
export function takipNoSorunu(no: string): string | null {
  const temiz = no.trim();
  if (temiz.length === 0) return 'Takip numarası boş olamaz.';
  if (temiz.length < 6) return 'Takip numarası çok kısa görünüyor — en az 6 karakter bekleniyor.';
  if (temiz.length > 60) return 'Takip numarası çok uzun görünüyor.';
  if (!/^[A-Za-z0-9][A-Za-z0-9 \-_]*$/.test(temiz)) {
    return 'Takip numarasında yalnızca harf, rakam, boşluk ve tire olabilir.';
  }
  return null;
}
